export type ImageSize = 'original' | 'medium' | 'thumbnail';

// Base URL for storage, configurable via environment variable
// Defaults to the API URL if not set, or falls back to a default if needed.
const STORAGE_BASE_URL = process.env.REACT_APP_STORAGE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Resolves a potentially relative image path to a full URL.
 * Handles:
 * - Full URLs (http/https): Returns as is
 * - Relative paths: Prepends STORAGE_BASE_URL
 * - Data URIs: Returns as is
 * - Empty/Undefined: Returns empty string
 */
export const resolveImageUrl = (path: string | undefined): string => {
  if (!path) return '';
  if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('blob:')) {
    return path;
  }
  
  // Ensure no leading slash for consistency
  let cleanPath = path.replace(/^\//, '');
  
  const baseUrl = STORAGE_BASE_URL.replace(/\/$/, '');
  
  if (baseUrl.includes('cloudfront.net') || baseUrl.includes('amazonaws.com') || baseUrl.includes('supabase.co')) {
    // CloudFront / AWS S3 / Supabase direct URLs do not have the 'uploads/' folder prefix
    cleanPath = cleanPath.replace(/^uploads\//, '');
  } else {
    // Prepends 'uploads/' if it's not already there
    // This is because all our files (users, opportunities, etc.) are in the 'uploads' bucket
    if (!cleanPath.startsWith('uploads/')) {
      cleanPath = `uploads/${cleanPath}`;
    }
  }
  
  return `${baseUrl}/${cleanPath}`;
};

/**
 * Transforms an image URL to the requested size version based on the backend storage convention.
 * 
 * Convention assumed:
 * - Original: .../original/filename_original.ext
 * - Medium: .../medium/filename_medium.ext
 * - Thumbnail: .../thumbnails/filename_thumb.ext (or .../thumbnail/...)
 * 
 * @param url The original image URL or path
 * @param size The desired size ('original', 'medium', 'thumbnail')
 * @returns The transformed URL or the original if transformation fails
 */
export const getOptimizedImageUrl = (url: string | undefined, size: ImageSize): string => {
  if (!url) return '';
  
  // First, resolve the full URL to work with
  let fullUrl = resolveImageUrl(url);
  
  if (fullUrl.includes('localhost:3500')) {
    const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://staging.13.204.65.150.nip.io';
    fullUrl = fullUrl.replace('http://localhost:3500', BACKEND_URL).replace('ws://localhost:3500', BACKEND_URL);
  }

  if (fullUrl.startsWith('data:') || fullUrl.startsWith('blob:')) return fullUrl; // Don't touch data URIs

  // If the URL is already the requested size, return it
  // (Simple check, can be improved)
  
  try {
    // Regex to capture parts: 
    // 1. Base path before the version folder
    // 2. Version folder (original|medium|thumbnails|thumbnail)
    // 3. Filename prefix (before the suffix)
    // 4. Suffix (_original|_medium|_thumb)
    // 5. Extension
    
    // Example: http://host/uploads/users/123/original/pic_original.jpg
    const regex = /^(.*\/)(original|medium|thumbnails|thumbnail)\/(.+)(_original|_medium|_thumb)(\.[^.]+)$/;
    const match = fullUrl.match(regex);

    if (!match) {
      // If it doesn't match our standard structure, return resolved URL
      return fullUrl;
    }

    const [, basePath, currentFolder, filenamePrefix, currentSuffix, extension] = match;

    let targetFolder = '';
    let targetSuffix = '';

    switch (size) {
      case 'medium':
        targetFolder = 'medium';
        targetSuffix = '_medium';
        break;
      case 'thumbnail':
        // Backend uses 'thumbnails' for users but 'thumbnail' for collaborations?
        // Let's check the input folder. If it was 'thumbnails' or 'original' in a user path, likely 'thumbnails'.
        // If it was 'thumbnail' in a collab path, likely 'thumbnail'.
        // To be safe, we might need to know the context or try to detect.
        // Based on UploadService: 
        // Users: "thumbnails" / "_thumb"
        // Collabs: "thumbnail" / "_thumb"
        
        // Heuristic: If current is 'thumbnails', keep plural. If 'thumbnail', keep singular.
        // If 'original', we need to guess. 
        // Let's look at the path. If it contains '/users/', it's likely 'thumbnails'.
        if (basePath.includes('/users/') && !basePath.includes('/collaborations/')) {
            targetFolder = 'thumbnails';
        } else {
            targetFolder = 'thumbnail';
        }
        targetSuffix = '_thumb';
        break;
      case 'original':
      default:
        targetFolder = 'original';
        targetSuffix = '_original';
        break;
    }

    return `${basePath}${targetFolder}/${filenamePrefix}${targetSuffix}${extension}`;

  } catch (error) {
    console.warn('Error optimizing image URL:', error);
    return fullUrl;
  }
};
